import { runtimeConfig } from "@src/runtime-config";
import { ExperimentalIcon, Dialog, IconButton, Text, Tooltip } from "@flat-ui";
import { useState } from "react";
import { useAppSelector } from "@store/store-helper";
import { RuntimeUtils } from "@stellar/utils";
import { DevFeaturesDialogContent } from "@components/dev-features-dialog/dev-features-dialog-content";
import { Box, Grid } from "@mui-wrapper";
import { appStateSelector } from "@store/app/app-slice";
import { shouldShowDevFeaturesButton } from "@components/dev-features-dialog/features-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ExperimentalFeatureEvents } from "@utils/track-event/track-event-list";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";

const isProduction = RuntimeUtils.isProductionEnvironment(runtimeConfig.appEnv);

interface Props {
  /** The roles of the current user */
  currentUserRoles: FaroCoreApiTypes.AdmUserRole[];
}

/**
 * Icon button to open the dialog for development features.
 * @returns An icon button opening the dev features dialog
 */
export function DevFeaturesDialogButton({
  currentUserRoles,
}: Props): React.JSX.Element | null {
  const [isOpen, setIsMenuOpen] = useState(false);
  const { trackEvent } = useTrackEvent();
  const { isDevModeEnabled, isBetaTestingEnabled } =
    useAppSelector(appStateSelector);

  // Checks if the button should be shown depending on the environment and flags
  if (
    !shouldShowDevFeaturesButton({
      isProduction,
      isDevModeEnabled,
      isBetaTestingEnabled,
    })
  ) {
    return null;
  }

  // Do not show the button in prod environment if the dev mode is not enabled or the loggedInUser is not internal user
  if (
    RuntimeUtils.isProductionEnvironment(runtimeConfig.appEnv) &&
    !isDevModeEnabled
  ) {
    return null;
  }

  function onExperimentalFeatureButtonClick(): void {
    trackEvent({ name: ExperimentalFeatureEvents.openDevTools });

    setIsMenuOpen(!isOpen);
  }

  const userRoles = currentUserRoles.map((role) => role).join(", ");

  return (
    <Box sx={{ ml: "0.75rem" }}>
      <Tooltip title="Experimental features">
        <IconButton onClick={onExperimentalFeatureButtonClick}>
          <ExperimentalIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        title="Experimental Features"
        open={isOpen}
        onClose={() => setIsMenuOpen(false)}
        size="l"
      >
        <Box>
          <Text
            variant="bodyM"
            sx={{
              display: "block",
              my: 2,
              mt: 0,
            }}
          >
            Debug and test the app with the following tools/features.
          </Text>

          <Grid container spacing={1} sx={{ justifyContent: "flex-start" }}>
            <Text shouldElide variant="bodyM">
              User Roles: {userRoles}
            </Text>

            <Tooltip title="Roles determine the level of rights you have in the AdminPanel.">
              <InfoOutlineIcon
                sx={{
                  height: "16px",
                  width: "16px",
                  color: "text.secondary",
                }}
              />
            </Tooltip>
          </Grid>
          <DevFeaturesDialogContent onNavigate={() => setIsMenuOpen(false)} />
        </Box>
      </Dialog>
    </Box>
  );
}
